/**
 * API Client for Havn Club Culinary App V2
 *
 * Multi-city, multi-week support with enhanced ordering features.
 */

const API_BASE = '/api'

// ══════════════════════════════════════════════════════════════════════
// BASE FETCH WRAPPER
// ══════════════════════════════════════════════════════════════════════

async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  }

  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    config.body = JSON.stringify(options.body)
  } else if (options.body instanceof FormData) {
    delete config.headers['Content-Type']
    config.body = options.body
  }

  const response = await fetch(url, config)

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

// ══════════════════════════════════════════════════════════════════════
// CITIES API
// ══════════════════════════════════════════════════════════════════════

export const citiesAPI = {
  list: () => fetchAPI('/menus/cities'),
}

// ══════════════════════════════════════════════════════════════════════
// MENUS API (V2 - Multi-city, Multi-week)
// ══════════════════════════════════════════════════════════════════════

export const menusAPI = {
  productionOrder: (menuId) => fetchAPI(`/menus/${menuId}/production-order`),
  saveProductionOrder: (menuId, dishIds, revision) => fetchAPI(`/menus/${menuId}/production-order`, {
    method: 'PUT', body: { dish_ids: dishIds, expected_revision: revision },
  }),
  // Get all menus for a city
  list: (cityId) => fetchAPI(`/menus/?city_id=${cityId}`),

  // Get a single menu with dishes
  get: (menuId) => fetchAPI(`/menus/${menuId}`),

  // Create a new menu — accepts either (cityId, name) or a single object { city_id, name, week_of }
  create: (cityIdOrData, name) => {
    const body = typeof cityIdOrData === 'object'
      ? cityIdOrData
      : { city_id: cityIdOrData, name }
    return fetchAPI('/menus/', {
      method: 'POST',
      body,
    })
  },

  // Update menu details
  update: (menuId, data) => fetchAPI(`/menus/${menuId}`, {
    method: 'PATCH',
    body: data,
  }),

  // Delete a menu
  delete: (menuId) => fetchAPI(`/menus/${menuId}`, {
    method: 'DELETE',
  }),

  // Update meal counts
  updateMealCounts: (menuId, mealCounts) => fetchAPI(`/menus/${menuId}/meal-counts`, {
    method: 'PUT',
    body: { meal_counts: mealCounts },
  }),

  // Add dish to menu
  addDish: (menuId, dishId, mealCount = 0) => fetchAPI(`/menus/${menuId}/dishes`, {
    method: 'POST',
    body: { dish_id: dishId, meal_count: mealCount },
  }),

  // Sync all dishes for a menu (atomic replace) — legacy format, no customizations
  syncDishes: (menuId, dishIds) => fetchAPI(`/menus/${menuId}/dishes/sync`, {
    method: 'PUT',
    body: { dish_ids: dishIds },
  }),

  // Sync dishes with per-menu customizations (component swaps for this week)
  syncDishesWithCustomizations: (menuId, dishes) => fetchAPI(`/menus/${menuId}/dishes/sync`, {
    method: 'PUT',
    body: { dishes },  // Array of { dish_id, customizations }
  }),

  // Update customizations for a single dish in a menu (e.g., from Menu Builder swap)
  updateDishCustomizations: (menuId, dishId, customizations) => fetchAPI(`/menus/${menuId}/dishes/${dishId}/customizations`, {
    method: 'PATCH',
    body: { customizations },
  }),

  // Remove dish from menu
  removeDish: (menuId, dishId) => fetchAPI(`/menus/${menuId}/dishes/${dishId}`, {
    method: 'DELETE',
  }),

  // Archive a menu
  archiveMenu: (menuId) => fetchAPI(`/menus/${menuId}/archive`, {
    method: 'POST',
  }),

  // Unarchive a menu
  unarchiveMenu: (menuId) => fetchAPI(`/menus/${menuId}/unarchive`, {
    method: 'POST',
  }),
}

// ══════════════════════════════════════════════════════════════════════
// DISHES API
// ══════════════════════════════════════════════════════════════════════

export const dishesAPI = {
  // Get all dishes (the dish library)
  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString()
    return fetchAPI(`/dishes${queryString ? `?${queryString}` : ''}`)
  },

  // Legacy: get all dishes via menus router
  listLegacy: () => fetchAPI('/menus/dishes/library'),

  // Get a single dish
  get: (dishId) => fetchAPI(`/dishes/${dishId}`),

  // Get top-ranked dishes per category
  topRanked: (params = {}) => {
    const queryString = new URLSearchParams(params).toString()
    return fetchAPI(`/dishes/top-ranked${queryString ? `?${queryString}` : ''}`)
  },

  // Get dish categories with counts
  categories: (cityId = 'dc') => fetchAPI(`/dishes/categories?city_id=${cityId}`),

  // Get cuisine types
  cuisines: (cityId = 'dc') => fetchAPI(`/dishes/cuisines?city_id=${cityId}`),

  // Get swap options for a component type
  swapOptions: (componentType, cityId = 'dc') =>
    fetchAPI(`/dishes/swap-options?component_type=${componentType}&city_id=${cityId}`),

  // Swap a component in a dish
  swapComponent: (dishId, componentId, data) =>
    fetchAPI(`/dishes/${dishId}/components/${componentId}/swap`, {
      method: 'PUT',
      body: data,
    }),

  // Bulk import dishes
  importBulk: (cityId, dishes) => fetchAPI('/dishes/import', {
    method: 'POST',
    body: { city_id: cityId, dishes },
  }),

  // Create a new dish
  create: (dish) => fetchAPI('/dishes', {
    method: 'POST',
    body: dish,
  }),

  // Update a dish (category, name, macros, etc.)
  update: (dishId, data) => fetchAPI(`/dishes/${dishId}`, {
    method: 'PATCH',
    body: data,
  }),
}

// ══════════════════════════════════════════════════════════════════════
// COMPONENTS API
// ══════════════════════════════════════════════════════════════════════

export const componentsAPI = {
  // Get all components grouped by type
  list: () => fetchAPI('/components/'),

  // Get swap pool options (proteins, vegetables, grains, sauces)
  getSwapPools: () => fetchAPI('/components/swaps'),
}

// ══════════════════════════════════════════════════════════════════════
// ORDERING API (V2 - Enhanced)
// ══════════════════════════════════════════════════════════════════════

export const orderingAPI = {
  // Calculate order quantities for a menu
  calculate: (menuId, showOnHand = true) =>
    fetchAPI(`/ordering/calculate/${menuId}?show_on_hand=${showOnHand}`),

  // Check for changes (multi-user sync polling)
  lastModified: (menuId) =>
    fetchAPI(`/ordering/v2/${menuId}/last-modified`),

  // Update on-hand quantity for an ingredient
  updateOnHand: (menuId, ingredientId, quantity, unit = 'lbs') =>
    fetchAPI(`/ordering/on-hand/${menuId}`, {
      method: 'PUT',
      body: { ingredient_id: ingredientId, quantity, unit },
    }),

  // Batch update on-hand quantities
  batchUpdateOnHand: (menuId, items) =>
    fetchAPI(`/ordering/on-hand/${menuId}/batch`, {
      method: 'PUT',
      body: { items },
    }),

  // Update supplier for an ingredient
  updateSupplier: (ingredientId, cityId, supplier, productUrl = null, notes = null) =>
    fetchAPI(`/ordering/ingredient/${ingredientId}/supplier`, {
      method: 'PUT',
      body: { city_id: cityId, supplier, product_url: productUrl, notes },
    }),

  // Update order factor (lbs/meal) for an ingredient
  // city_id persists the value to ingredient_city_config for future menus
  updateOrderFactor: (ingredientId, orderFactorLbs, cityId = null, menuId = null) =>
    fetchAPI(`/ordering/ingredient/${ingredientId}/order-factor`, {
      method: 'PUT',
      body: { order_factor_lbs: orderFactorLbs, city_id: cityId, menu_id: menuId },
    }),

  // Update raw ingredient name for protein components
  updateRawIngredientName: (currentName, newName) =>
    fetchAPI(`/ordering/ingredient/raw-name/${encodeURIComponent(currentName)}`, {
      method: 'PUT',
      body: { raw_ingredient_name: newName, component_type: 'protein' },
    }),

  // Mark item as ordered
  markOrdered: (menuId, ingredientId, ordered = true) =>
    fetchAPI(`/ordering/ordered/${menuId}`, {
      method: 'PUT',
      body: { ingredient_id: ingredientId, ordered },
    }),

  // Update amount ordered for an ingredient
  updateAmountOrdered: (menuId, ingredientId, amountOrdered, lbsPerMeal = null) =>
    fetchAPI(`/ordering/amount-ordered/${menuId}`, {
      method: 'PUT',
      body: { ingredient_id: ingredientId, amount_ordered: amountOrdered, lbs_per_meal: lbsPerMeal },
    }),

  // Get column configuration
  getColumns: () => fetchAPI('/ordering/columns'),

  // Save column configuration
  saveColumns: (columns) => fetchAPI('/ordering/columns', {
    method: 'PUT',
    body: { columns },
  }),

  // Get ingredient details
  getIngredientDetail: (ingredientId, cityId) =>
    fetchAPI(`/ordering/ingredient/${ingredientId}?city_id=${cityId}`),

  // Get split orders for a menu
  getSplitOrders: (menuId) => fetchAPI(`/ordering/split-orders/${menuId}`),

  // Update split orders for an ingredient
  updateSplitOrders: (menuId, ingredientId, splits) =>
    fetchAPI(`/ordering/split-orders/${menuId}/${ingredientId}`, {
      method: 'PUT',
      body: { splits },
    }),

  // Update is_on_hand status (mark as "we have plenty")
  updateIsOnHand: (menuId, ingredientId, isOnHand) =>
    fetchAPI(`/ordering/is-on-hand/${menuId}`, {
      method: 'PUT',
      body: { ingredient_id: ingredientId, is_on_hand: isOnHand },
    }),

  // Update display unit for an ingredient
  updateDisplayUnit: (menuId, ingredientId, displayUnit) =>
    fetchAPI(`/ordering/display-unit/${menuId}`, {
      method: 'PUT',
      body: { ingredient_id: ingredientId, display_unit: displayUnit },
    }),

  // Get available display units
  getDisplayUnits: () => fetchAPI('/ordering/display-units'),

  // ═══════════════════════════════════════════════════════════════════
  // AUTO-SAVE & RECOVERY
  // ═══════════════════════════════════════════════════════════════════

  // Create a snapshot of current ordering state
  createSnapshot: (menuId, orderingData, userIdentifier = null) =>
    fetchAPI(`/ordering/snapshot/${menuId}`, {
      method: 'POST',
      body: { ordering_data: orderingData, user_identifier: userIdentifier },
    }),

  // Bulk save all ordering data at once
  bulkSave: (menuId, ingredients, userIdentifier = null) =>
    fetchAPI(`/ordering/bulk-save/${menuId}`, {
      method: 'POST',
      body: { ingredients, user_identifier: userIdentifier },
    }),

  // List recent snapshots for recovery
  listSnapshots: (menuId, limit = 20) =>
    fetchAPI(`/ordering/snapshots/${menuId}?limit=${limit}`),

  // Get a specific snapshot
  getSnapshot: (menuId, snapshotId) =>
    fetchAPI(`/ordering/snapshot/${menuId}/${snapshotId}`),

  // Restore from a snapshot
  restoreSnapshot: (menuId, snapshotId) =>
    fetchAPI(`/ordering/restore/${menuId}/${snapshotId}`, {
      method: 'POST',
    }),
}

// ══════════════════════════════════════════════════════════════════════
// INVENTORY IMPORT API
// ══════════════════════════════════════════════════════════════════════

export const inventoryAPI = {
  // Import inventory from CSV/Excel
  import: (menuId, cityId, file) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('city_id', cityId)
    return fetchAPI(`/inventory/import/${menuId}`, {
      method: 'POST',
      body: formData,
    })
  },

  // Get all imported inventory items for a menu
  getItems: (menuId) => fetchAPI(`/inventory/items/${menuId}`),

  // Update an inventory item (quantity, unit, is_on_hand)
  updateItem: (menuId, itemId, data) => fetchAPI(`/inventory/items/${menuId}/${itemId}`, {
    method: 'PUT',
    body: data,
  }),

  // Clear all inventory for a menu
  clear: (menuId) => fetchAPI(`/inventory/clear/${menuId}`, {
    method: 'POST',
  }),

  // Export inventory as JSON
  export: (menuId) => fetchAPI(`/inventory/export/${menuId}`),
}

// ══════════════════════════════════════════════════════════════════════
// RECIPES API
// ══════════════════════════════════════════════════════════════════════

export const recipesAPI = {
  // Browse all recipes grouped by category (for meal builder)
  browser: (cityId = 'dc') => fetchAPI(`/recipes/browser?city_id=${encodeURIComponent(cityId)}`),

  // List all recipes
  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString()
    return fetchAPI(`/recipes${queryString ? `?${queryString}` : ''}`)
  },

  // Get recipes for a specific menu (current menu filter)
  forMenu: (menuId) => fetchAPI(`/recipes/for-menu/${menuId}`),

  // Get a single recipe
  get: (recipeId) => fetchAPI(`/recipes/${recipeId}`),

  // Create a new recipe
  create: (recipe) => fetchAPI('/recipes', {
    method: 'POST',
    body: recipe,
  }),

  // Update a recipe
  update: (recipeId, recipe) => fetchAPI(`/recipes/${recipeId}`, {
    method: 'PUT',
    body: recipe,
  }),

  // Delete a recipe
  delete: (recipeId) => fetchAPI(`/recipes/${recipeId}`, {
    method: 'DELETE',
  }),

  // Scale a recipe
  scale: (recipeId, targetQuantity, targetUnit) => {
    const params = new URLSearchParams({ target_quantity: targetQuantity })
    if (targetUnit) params.append('target_unit', targetUnit)
    return fetchAPI(`/recipes/${recipeId}/scale?${params}`)
  },

  // Get all categories
  getCategories: () => fetchAPI('/recipes/categories/list'),
}

// ══════════════════════════════════════════════════════════════════════
// TRANSLATION API
// ══════════════════════════════════════════════════════════════════════

export const translateAPI = {
  // Translate a single text
  single: (text, targetLang = 'es') =>
    fetchAPI('/translate', {
      method: 'POST',
      body: { text, target_language: targetLang },
    }),

  // Translate a batch of texts
  batch: (texts, targetLang = 'es') =>
    fetchAPI('/translate/batch', {
      method: 'POST',
      body: { texts, target_language: targetLang },
    }),

  // Translate an entire page structure
  page: (content, targetLang = 'es') =>
    fetchAPI('/translate/page', {
      method: 'POST',
      body: { content, target_language: targetLang },
    }),

  // Cached bulk recipe translation for a city. Returns
  //   { recipes: { [id]: { name, prep_instructions, ... } }, stats, error? }
  recipesBulk: (cityId, language = 'spanish') =>
    fetchAPI('/translate/recipes/bulk', {
      method: 'POST',
      body: { city_id: cityId, language },
    }),

  // Cached bulk dish translation for a city. Returns
  //   { dishes: { [id]: { name, description, chef_notes } }, stats, error? }
  dishesBulk: (cityId, language = 'spanish') =>
    fetchAPI('/translate/dishes/bulk', {
      method: 'POST',
      body: { city_id: cityId, language },
    }),

  // Fetch pre-translated UI labels (Chef Notes, Ingredients, etc.).
  commonTerms: (language = 'spanish') =>
    fetchAPI(`/translate/common-terms/${language}`),
}

// ══════════════════════════════════════════════════════════════════════
// EXPORT API
// ══════════════════════════════════════════════════════════════════════

// Build a query string fragment with an optional scale and/or language param.
// Language is only attached when it's a non-English language so existing
// export URLs stay byte-identical for the English path.
function _exportQS({ scaleTo = null, language = null, menuId = null } = {}) {
  const parts = []
  if (scaleTo) parts.push(`scale_to=${scaleTo}`)
  if (menuId != null) parts.push(`menu_id=${menuId}`)
  if (language && language !== 'english') parts.push(`language=${language}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

export const exportAPI = {
  // Open order sheet in new tab for printing
  openOrderSheet: (menuId, language = null) => {
    window.open(`${API_BASE}/export/order-sheet/${menuId}${_exportQS({ language })}`, '_blank')
  },

  // Open recipe card in new tab for printing
  openRecipeCard: (recipeId, scaleTo = null, language = null, menuId = null) => {
    window.open(`${API_BASE}/export/recipe-card/${recipeId}${_exportQS({ scaleTo, language, menuId })}`, '_blank')
  },

  // Open prep sheet in new tab for printing
  openPrepSheet: (menuId, language = null) => {
    window.open(`${API_BASE}/export/prep-sheet/${menuId}${_exportQS({ language })}`, '_blank')
  },

  // Open all recipes for bulk print
  openAllRecipes: (menuId, language = null) => {
    window.open(`${API_BASE}/export/all-recipes/${menuId}${_exportQS({ language })}`, '_blank')
  },
}

// ══════════════════════════════════════════════════════════════════════
// AIRTABLE IMPORT API (Meal Counts + Customizations)
// ══════════════════════════════════════════════════════════════════════

export const airtableAPI = {
  // Import current week's orders from Airtable (triggers AI analysis)
  // menuId scopes the import so different menus don't share data.
  // background=true runs it as a background job (returns immediately with
  // {status:"started"}); poll getImportStatus, then read getLatest. Use this
  // from the browser — the synchronous import can take 1–2+ minutes, longer
  // than a browser will hold the request open.
  // scope ('confirmed' | 'paid') applies to Havn OS cities only.
  importOrders: (cityId, menuId, background = false, scope = null) => {
    const params = new URLSearchParams()
    if (menuId) params.set('menu_id', menuId)
    if (background) params.set('background', '1')
    if (scope) params.set('scope', scope)
    const qs = params.toString()
    return fetchAPI(`/airtable/import/${cityId}${qs ? `?${qs}` : ''}`, { method: 'POST' })
  },

  // Whether the city's import is locked (counts stay as last imported)
  getImportLock: (cityId) => fetchAPI(`/airtable/import-lock/${cityId}`),

  // Check import status
  getImportStatus: (cityId) => fetchAPI(`/airtable/import-status/${cityId}`),

  // Get the latest import for a specific menu (prevents cross-menu data bleed)
  getLatest: (cityId, menuId) => {
    const params = menuId ? `?menu_id=${menuId}` : ''
    return fetchAPI(`/airtable/latest/${cityId}${params}`)
  },
}

// ══════════════════════════════════════════════════════════════════════
// PLATED API (Havn OS count lock and waiting orders — Miami / Phoenix)
// ══════════════════════════════════════════════════════════════════════

export const platedAPI = {
  get: (cityId, menuId) => fetchAPI(`/havn-os/plated/${cityId}?menu_id=${menuId}`),
  // name: lock_counts | set_plated_counts | set_admission | decide_order. refresh re-runs the import afterwards.
  run: (cityId, name, { menuId, operator, input, refresh = false, idempotencyKey }) =>
    fetchAPI(`/havn-os/plated/${cityId}/actions/${name}`, {
      method: 'POST',
      body: { menu_id: menuId, operator, input, refresh, idempotency_key: idempotencyKey },
    }),
  refresh: (cityId, { menuId, operator }) =>
    fetchAPI(`/havn-os/plated/${cityId}/refresh`, { method: 'POST', body: { menu_id: menuId, operator } }),
}

// ══════════════════════════════════════════════════════════════════════
// WEIGHTS API (shared plating weigh-ins — Weights tab)
// ══════════════════════════════════════════════════════════════════════

export const weightsAPI = {
  // All shared weigh-ins for a menu → { onHand:{}, unit:{}, piece:{} }
  get: (cityId, menuId) => fetchAPI(`/weights/${cityId}/${menuId}`),

  // Cheap change marker for multi-user sync polling
  lastModified: (cityId, menuId) => fetchAPI(`/weights/${cityId}/${menuId}/last-modified`),

  // Upsert one cell (empty/null value clears it)
  set: (cityId, menuId, section, key, value) =>
    fetchAPI(`/weights/${cityId}/${menuId}`, {
      method: 'PUT',
      body: { section, key, value },
    }),

  // Clear every weigh-in for a menu
  clear: (cityId, menuId) => fetchAPI(`/weights/${cityId}/${menuId}`, {
    method: 'DELETE',
  }),
}

// ══════════════════════════════════════════════════════════════════════
// LEGACY COMPATIBILITY - Map old API structure to new
// ══════════════════════════════════════════════════════════════════════

// For backwards compatibility with old pages during transition
export const menuAPI = {
  getMenu: () => fetchAPI('/menu/'),
  updateMenu: (menu) => fetchAPI('/menu/', { method: 'PUT', body: menu }),
  updateMealCounts: (mealCounts) => fetchAPI('/menu/meal-counts/', { method: 'PUT', body: { meal_counts: mealCounts } }),
  getMealCounts: () => fetchAPI('/menu/meal-counts/'),
}

// ══════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ══════════════════════════════════════════════════════════════════════

export function formatQuantity(value, decimals = 1) {
  if (value === null || value === undefined) return '—'
  if (value === 0) return '0'
  if (Math.abs(value) < 0.01) return '<0.01'
  // Show at most 1 decimal place, remove trailing zeros
  return value.toFixed(decimals).replace(/\.?0+$/, '')
}

export function getCategoryColor(category) {
  const colors = {
    protein: 'badge-protein',
    proteins: 'badge-protein',
    carb: 'badge-carb',
    carbs: 'badge-carb',
    vegetable: 'badge-vegetable',
    vegetables: 'badge-vegetable',
    sauce: 'badge-sauce',
    sauces: 'badge-sauce',
    liquid: 'badge-liquid',
    liquids: 'badge-liquid',
    dry: 'badge-dry',
    dry_goods: 'badge-dry',
    refrigerated: 'badge-refrigerated',
    base: 'badge-base',
    bases: 'badge-base',
    chicken: 'badge-chicken',
    chicken_2: 'badge-chicken',
    beef: 'badge-beef',
    seafood: 'badge-seafood',
    seafood_2: 'badge-seafood',
    veg: 'badge-veg',
    chef: 'badge-chef',
    cheat: 'badge-cheat',
    pasta: 'badge-pasta',
    salad: 'badge-salad',
    salad_2: 'badge-salad',
    broth: 'badge-broth',
    broth_2: 'badge-broth',
    oats: 'badge-chia',
    chia: 'badge-chia',
    chia_2: 'badge-chia',
    wellness_shots: 'badge-wellness',
    wellness: 'badge-wellness',
    date_balls: 'badge-cheat',
    grain: 'badge-carb',
    seasoning: 'badge-dry',
  }
  return colors[category?.toLowerCase()] || 'badge-base'
}


// Default supplier options (fallback if backend unavailable)
// These are fetched dynamically per city from the backend
export const SUPPLIERS = ['US Foods', 'Baldor', 'Local']

// Display unit options with conversion factors from lbs
export const DISPLAY_UNITS = {
  lbs: { factor: 1.0, label: 'lbs' },
  kg: { factor: 0.453592, label: 'kg' },
  oz: { factor: 16.0, label: 'oz' },
  liters: { factor: 0.453592, label: 'L' },
  gallons: { factor: 0.119826, label: 'gal' },
  ml: { factor: 453.592, label: 'mL' },
}

// Convert value from lbs to display unit
export function convertFromLbs(value, targetUnit) {
  if (!targetUnit || !DISPLAY_UNITS[targetUnit]) return value
  return value * DISPLAY_UNITS[targetUnit].factor
}

// Convert value from display unit back to lbs
export function convertToLbs(value, sourceUnit) {
  if (!sourceUnit || !DISPLAY_UNITS[sourceUnit]) return value
  return value / DISPLAY_UNITS[sourceUnit].factor
}

// Get display unit label
export function getUnitLabel(unit) {
  if (!unit || !DISPLAY_UNITS[unit]) return 'lbs'
  return DISPLAY_UNITS[unit].label
}

// Supplier API - fetches city-specific suppliers
export const suppliersAPI = {
  getForCity: (cityId) => fetchAPI(`/ordering/suppliers/${cityId}`),
}

// Default columns for ordering table
export const DEFAULT_COLUMNS = [
  { id: 'ingredient', label: 'Ingredient', visible: true, locked: true },
  { id: 'used_in', label: 'Used In', visible: true },
  { id: 'meal', label: 'Meal', visible: false },  // Hidden by default - shows which dish
  { id: 'meal_count', label: 'Meal Count', visible: true },
  { id: 'lbs_per_meal', label: 'Lbs/Meal', visible: true },
  { id: 'needed', label: 'Needed', visible: true },
  { id: 'on_hand', label: 'On Hand', visible: true },
  { id: 'to_order', label: 'To Order', visible: true },
  { id: 'ordered', label: 'Ordered', visible: true },
  { id: 'supplier', label: 'Supplier', visible: true },
  { id: 'notes', label: 'Notes', visible: false },
]



// ══════════════════════════════════════════════════════════════════════
// PRINT MATERIALS API — weekly print pack (cards + stickers) per menu
// ══════════════════════════════════════════════════════════════════════

export const printMaterialsAPI = {
  // Everything saved for a menu + the other city's menu for the same week
  get: (menuId) => fetchAPI(`/print-materials/menu/${menuId}`),

  // formData: files[] (+ optional kinds/labels JSON lists, mirror, replace)
  upload: (menuId, formData) => fetchAPI(`/print-materials/menu/${menuId}/upload`, {
    method: 'POST',
    body: formData,
  }),

  update: (assetId, data) => fetchAPI(`/print-materials/assets/${assetId}`, {
    method: 'PATCH',
    body: data,
  }),

  remove: (assetId, { mirror = false } = {}) => fetchAPI(`/print-materials/assets/${assetId}?mirror=${mirror}`, {
    method: 'DELETE',
  }),

  fileUrl: (assetId) => `${API_BASE}/print-materials/assets/${assetId}/file`,
  downloadUrl: (assetId) => `${API_BASE}/print-materials/assets/${assetId}/file?download=1`,
  zipUrl: (menuId) => `${API_BASE}/print-materials/menu/${menuId}/zip`,
}
